import React, { useState } from "react";
import "../../settings.styles.css";
import "./statusViewEditComponent.styles.scss";
import { Input, Label } from "reactstrap";
import Select from "react-select";
import { isEmpty } from "lodash";
import { RepeatIntervalInput } from "../../../../Components/Common/RepeatInterval";
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  restrictToParentElement,
  restrictToVerticalAxis,
} from "@dnd-kit/modifiers";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

// A single reorderable status row. Provides the @dnd-kit sortable wiring and a
// drag-handle grip; the row's contents are supplied by the parent through a
// render-prop so the (large) inline row JSX stays in one place. Only used when
// the list is `reorderable` and no search filter is active.
function SortableRow({ id, children }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 20 : undefined,
    position: "relative",
  };
  const dragHandle = (
    <button
      type="button"
      className={"settings-status-list__drag-handle me-1"}
      title={"Drag to reorder"}
      {...attributes}
      {...listeners}
    >
      <i className="ri-drag-move-2-line" />
    </button>
  );
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={
        "settings-status-list__row d-flex flex-row align-items-center justify-content-start" +
        (isDragging ? " settings-status-list__row--dragging" : "")
      }
    >
      {children(dragHandle)}
    </div>
  );
}

// Change #100482 - for companies that set a job complete first and invoice
// it afterwards.
const ALLOW_UNBILLED_CLOSE_HINT =
  "Lets a job be moved to this status while billable, predicted or purchase items are still unbilled. Leave unticked to keep the check. Open tasks still block closing either way.";

export default function StatusViewEditComponent({
  setStatusList,
  statusList,
  createStatus,
  setCreateStatus,
  handleCreateNewStatus,
  setDeleteStatus,
  setDeleteModal,
  setUpdateItemStatus,
  costOfGoodsSold,
  config,
  statusFlag,
  paymentTerm, //true false
  logicType, // [select option]
  bankAccountStatus, //bank account status true false
  mileageBased, //company vehicle check type FlagMileageBased true false
  // Change #100482 - Job Status only: FlagAllowUnbilledClose true false
  allowUnbilledCloseFlag = false,
  // Change #100439 - company vehicle check type default Form + repeat days.
  // { formOptions: [{value,label}] } to show them, null/undefined to hide.
  checkTypeSchedule,
  taskCustomStatus = false,
  taskTemplateList = [],
  reorderable = false,
  onReorder,
  searchable = true,
}) {
  const [search, setSearch] = useState("");
  const searchTerm = search.trim().toLowerCase();
  const searchActive = searchTerm.length > 0;
  // Drag-and-drop is only offered when the caller opts in AND no search filter
  // is active — reordering a filtered subset would produce ambiguous Order values.
  const dndEnabled = reorderable && !searchActive;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const matchesSearch = (item) =>
    !searchActive || (item?.Name || "").toLowerCase().includes(searchTerm);

  const visibleCount = (statusList || []).filter(matchesSearch).length;

  // Change #100439 - the check type's form and repeat schedule, copied onto
  // each new check of that type. Used on the create row and on every row.
  const renderCheckTypeSchedule = (row, onChange) => {
    if (!checkTypeSchedule) return null;
    const options = checkTypeSchedule.formOptions || [];
    // Vehicle types reuse this for their handover form (HandoverForm_Id, no repeat).
    const formField = checkTypeSchedule.formField || "Form_Id";
    const showRepeat = checkTypeSchedule.showRepeat !== false;
    return (
      <>
        <div className={"me-2"} style={{ width: "220px" }}>
          <Select
            placeholder={checkTypeSchedule.placeholder || "Form (optional)"}
            isClearable
            options={options}
            value={
              options.find((o) => o.value === String(row?.[formField] || "")) ||
              null
            }
            onChange={(e) => onChange({ [formField]: e?.value || "" })}
            menuPortalTarget={document.body}
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 2000 }) }}
          />
        </div>
        {showRepeat && (
        <div
          className={"me-2 d-flex align-items-center"}
          title={"How often this check comes round. Blank = one-off."}
        >
          <span className={"me-1 text-nowrap"}>Repeat every</span>
          {/* Change #100677 - day / week / month / year. */}
          <RepeatIntervalInput
            size="sm"
            days={row?.RepeatIntervalDays}
            unit={row?.RepeatIntervalUnit}
            onChange={(patch) => onChange(patch)}
          />
        </div>
        )}
      </>
    );
  };

  // Renumber Order to match the new position and hand the full list back to the
  // caller to persist. Only reached in dndEnabled mode.
  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || String(active.id) === String(over.id)) return;
    const oldIndex = (statusList || []).findIndex(
      (s) => String(s.Id) === String(active.id)
    );
    const newIndex = (statusList || []).findIndex(
      (s) => String(s.Id) === String(over.id)
    );
    if (oldIndex < 0 || newIndex < 0) return;
    const reordered = arrayMove(statusList, oldIndex, newIndex).map((s, i) => ({
      ...s,
      Order: i,
    }));
    setStatusList(reordered);
    onReorder?.(reordered);
  };

  // A non-interactive grip shown when reorderable but a search filter is active.
  const staticGrip = (
    <span
      className={
        "settings-status-list__drag-handle settings-status-list__drag-handle--disabled me-1"
      }
      title={"Clear search to reorder"}
    >
      <i className="ri-drag-move-2-line" />
    </span>
  );

  return (
    // Layout and behaviour are untouched; `settings-status-list` only carries
    // the styling that turns the add row into a card and each status into a
    // hoverable row. 28 settings panels render this component.
    <div className={"mt-2 pt-2 settings-status-list"}>
      <div>
        <div
          className={
            "settings-status-list__new d-flex flex-row align-items-center justify-content-start pb-2"
          }
        >
          <div>
            <span className={"d-flex pe-2"} style={{ width: "45px" }}>
              <span className={"text-success"}>New</span>
            </span>
          </div>
          <div className={"p-1"}>
            <div
              className={"badge d-flex align-items-center"}
              style={{
                color: createStatus?.CssColourText ?? "#000000",
                background: createStatus?.CssColourBackground ?? "#ffffff",
              }}
            >
              <div
                className={
                  "d-flex align-items-center cursor-pointer status-edit-and-name-block"
                }
                title={"Click to edit"}
                onClick={() => {
                  // set as editable true and others false
                  setCreateStatus((prev) => {
                    const temp = { ...prev };
                    temp.editable = true;
                    return temp;
                  });
                }}
              >
                {createStatus?.editable ? (
                  <input
                    autoFocus={true}
                    className={"w-100"}
                    type="text"
                    value={createStatus?.Name ?? ""}
                    onChange={(e) => {
                      setCreateStatus((prev) => {
                        const temp = { ...prev };
                        temp.Name = e.target.value;
                        return temp;
                      });
                    }}
                    onBlur={() => {
                      setCreateStatus((prev) => {
                        const temp = { ...prev };
                        temp.editable = false;
                        return temp;
                      });
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        setCreateStatus((prev) => {
                          const temp = { ...prev };
                          temp.editable = false;
                          return temp;
                        });
                      }
                    }}
                  />
                ) : (
                  <span className={""}>
                    {createStatus?.Name?.length > 30
                      ? createStatus?.Name?.substring(0, 30) + "..."
                      : createStatus?.Name}
                  </span>
                )}
              </div>
            </div>
          </div>
          {config?.name === "MasterPaymentProvider" && (
            <div
              className={"d-flex align-items-center"}
              style={{ width: "150px" }}
            >
              <Select
                className={"w-100 p-0 m-0"}
                value={
                  createStatus?.MasterPaymentProvider_Id
                    ? {
                        value: createStatus?.MasterPaymentProvider_Id,
                        label: config.data?.find(
                          (item) =>
                            item.Id === createStatus?.MasterPaymentProvider_Id
                        )?.Name,
                        id: createStatus?.MasterPaymentProvider_Id,
                      }
                    : ""
                }
                placeholder={"Select"}
                onChange={(e) => {
                  setCreateStatus((prev) => {
                    const temp = { ...prev };
                    temp.MasterPaymentProvider_Id = e.value;
                    return temp;
                  });
                }}
                options={config.data?.map((item) => ({
                  value: item.Id,
                  label: item.Name,
                  id: item.Id,
                }))}
              ></Select>
            </div>
          )}
          {config?.name === "MasterExternalIdentificationProvider" && (
            <div
              className={"d-flex align-items-center"}
              style={{ width: "180px" }}
            >
              <Select
                className={"w-100 p-0 m-0"}
                value={
                  createStatus?.MasterExternalIdentificationProvider_Id
                    ? {
                        value:
                          createStatus?.MasterExternalIdentificationProvider_Id,
                        label: config.data?.find(
                          (item) =>
                            item.Id ===
                            createStatus?.MasterExternalIdentificationProvider_Id
                        )?.Name,
                        id: createStatus?.MasterExternalIdentificationProvider_Id,
                      }
                    : ""
                }
                placeholder={"Map to master…"}
                onChange={(e) => {
                  setCreateStatus((prev) => ({
                    ...prev,
                    MasterExternalIdentificationProvider_Id: e.value,
                  }));
                }}
                options={config.data?.map((item) => ({
                  value: item.Id,
                  label: item.Name,
                  id: item.Id,
                }))}
              ></Select>
            </div>
          )}
          {config?.Name === "generalSettingsFrequency" && (
            <div className={"d-flex align-items-center"}>
              <div className={"ms-2 me-2"}>
                <button
                  className={
                    createStatus?.IntervalValueMathSign === "+"
                      ? "btn btn-sm btn-success me-1"
                      : "btn btn-sm btn-soft-success me-1"
                  }
                  onClick={() => {
                    setCreateStatus((prev) => {
                      const temp = { ...prev };
                      temp.IntervalValueMathSign = "+";
                      return temp;
                    });
                  }}
                >
                  Add
                </button>{" "}
                <button
                  className={
                    createStatus?.IntervalValueMathSign === "-"
                      ? "btn btn-sm btn-danger"
                      : "btn btn-sm btn-soft-danger"
                  }
                  onClick={() => {
                    setCreateStatus((prev) => {
                      const temp = { ...prev };
                      temp.IntervalValueMathSign = "-";
                      return temp;
                    });
                  }}
                >
                  Minus
                </button>
              </div>
              <div>
                <span>Value </span>
                <input
                  type={"number"}
                  className={"p-2"}
                  style={{ width: "50px" }}
                  value={createStatus?.IntervalValue}
                  onChange={(e) => {
                    setCreateStatus((prev) => {
                      const temp = { ...prev };
                      temp.IntervalValue = e.target.value;
                      return temp;
                    });
                  }}
                />
              </div>
              <div className={"  ms-2 me-2"} style={{ width: "100px" }}>
                <Select
                  className={"w-100 p-0 m-0"}
                  value={{
                    value: createStatus?.MasterTimeFrameInterval_Id,
                    label: config.data?.find(
                      (item) =>
                        item.Id === createStatus?.MasterTimeFrameInterval_Id
                    )?.Name,
                    id: createStatus?.MasterTimeFrameInterval_Id,
                  }}
                  onChange={(e) => {
                    setCreateStatus((prev) => {
                      const temp = { ...prev };
                      temp.MasterTimeFrameInterval_Id = e.value;
                      return temp;
                    });
                  }}
                  options={config.data?.map((item) => ({
                    value: item.Id,
                    label: item.Name,
                    id: item.Id,
                  }))}
                ></Select>
              </div>
            </div>
          )}

          {/*  colorpicker input for text color*/}
          <div className={"ms-2 me-2 d-flex align-items-center"}>
            <span>Text Colour: </span>
            <input
              style={{ background: createStatus?.CssColourText }}
              className={"color-picker-like-input-circle"}
              value={createStatus?.CssColourText ?? "#000000"}
              type="color"
              onChange={(e) => {
                setCreateStatus((prev) => {
                  const temp = { ...prev };
                  temp.CssColourText = e.target.value;
                  return temp;
                });
              }}
            />
          </div>
          <div className={"m-0 ms-2 me-2 d-flex align-items-center"}>
            <span>Background Colour: </span>
            <input
              style={{ background: createStatus?.CssColourBackground }}
              className={"color-picker-like-input-circle"}
              value={createStatus?.CssColourBackground ?? "#ffffff"}
              type="color"
              onChange={(e) => {
                setCreateStatus((prev) => {
                  const temp = { ...prev };
                  temp.CssColourBackground = e.target.value;
                  return temp;
                });
              }}
            />
          </div>
          {costOfGoodsSold && (
            <div className={"m-0 ms-2 me-2 d-flex align-items-center"}>
              {/*  check box*/}

              <span className={"d-flex align-items-center me-2"}>
                Cost of goods sold
              </span>
              <input
                type="checkbox"
                className="form-check-input m-0"
                checked={createStatus?.FlagCostOfGoodsSold === "1"}
                onChange={(e) => {
                  setCreateStatus((prev) => {
                    const temp = { ...prev };
                    temp.FlagCostOfGoodsSold = e.target.checked ? "1" : "0";
                    return temp;
                  });
                }}
              />
            </div>
          )}
          {(statusFlag || bankAccountStatus) && (
            <div className={"m-0 ms-2 me-2 d-flex align-items-center"}>
              {/*  check box*/}
              <span className={"d-flex align-items-center me-2"}>
                {statusFlag && <span>Mark as Status Complete</span>}
                {bankAccountStatus && <span>Active</span>}
              </span>
              <input
                type="checkbox"
                className="form-check-input m-0"
                checked={createStatus?.FlagActive === "1"}
                onChange={(e) => {
                  setCreateStatus((prev) => {
                    const temp = { ...prev };
                    temp.FlagActive = e.target.checked ? "1" : "0";
                    return temp;
                  });
                }}
              />
            </div>
          )}
          {statusFlag && (
            <div className={"m-0 ms-2 me-2 d-flex align-items-center"}>
              {/*  check box*/}
              <span className={"d-flex align-items-center me-2"}>
                Invoiceable
              </span>
              <input
                type="checkbox"
                className="form-check-input m-0"
                checked={createStatus?.FlagInvoiceable === "1"}
                onChange={(e) => {
                  setCreateStatus((prev) => {
                    const temp = { ...prev };
                    temp.FlagInvoiceable = e.target.checked ? "1" : "0";
                    return temp;
                  });
                }}
              />
            </div>
          )}
          {allowUnbilledCloseFlag && (
            <div
              className={"m-0 ms-2 me-2 d-flex align-items-center"}
              title={ALLOW_UNBILLED_CLOSE_HINT}
            >
              <span className={"d-flex align-items-center me-2"}>
                Allow closing with unbilled items
              </span>
              <input
                type="checkbox"
                className="form-check-input m-0"
                checked={createStatus?.FlagAllowUnbilledClose === "1"}
                onChange={(e) => {
                  setCreateStatus((prev) => {
                    const temp = { ...prev };
                    temp.FlagAllowUnbilledClose = e.target.checked ? "1" : "0";
                    return temp;
                  });
                }}
              />
            </div>
          )}
          {mileageBased && (
            <div className={"m-0 ms-2 me-2 d-flex align-items-center"}>
              {/*  check box*/}
              <span className={"d-flex align-items-center me-2"}>
                Mileage Based
              </span>
              <input
                type="checkbox"
                className="form-check-input m-0"
                checked={createStatus?.FlagMileageBased === "1"}
                onChange={(e) => {
                  setCreateStatus((prev) => {
                    const temp = { ...prev };
                    temp.FlagMileageBased = e.target.checked ? "1" : "0";
                    return temp;
                  });
                }}
              />
            </div>
          )}
          {renderCheckTypeSchedule(createStatus, (patch) =>
            setCreateStatus((prev) => ({ ...prev, ...patch }))
          )}
          {paymentTerm && (
            <div style={{ width: "350px" }} className={"me-2"}>
              <div className={"d-flex flex-row align-items-center  "}>
                <div className={"me-1"} style={{ width: "100px" }}>
                  Logic Type
                </div>
                <Select
                  placeholder={"Select Logic Type"}
                  className={"form-control  p-0 m-0"}
                  value={
                    createStatus?.LogicType
                      ? {
                          value: createStatus?.LogicType,
                          label: logicType?.find(
                            (logic) => logic.value === createStatus?.LogicType
                          )?.label,
                        }
                      : []
                  }
                  onChange={(e) => {
                    setCreateStatus((prev) => {
                      const temp = { ...prev };
                      temp.LogicType = e.value;
                      return temp;
                    });
                  }}
                  options={logicType}
                ></Select>
              </div>
            </div>
          )}
          {paymentTerm &&
            (createStatus?.LogicType === "1" ||
              createStatus?.LogicType === "2" ||
              createStatus?.LogicType === "3") && (
              <div className={"me-2"}>
                <div className={"d-flex flex-row align-items-center  "}>
                  <div className={"me-1"}>Duration</div>
                  <input
                    type={"number"}
                    className={"p-2"}
                    style={{ width: "50px" }}
                    value={createStatus?.LogicValue}
                    onChange={(e) => {
                      setCreateStatus((prev) => {
                        const temp = { ...prev };
                        temp.LogicValue = e.target.value;
                        return temp;
                      });
                    }}
                  />
                </div>
              </div>
            )}
          <div>
            <button
              title={"Click to create"}
              className={"btn btn-sm btn-success status-recycling-bin-icon"}
              onClick={() => {
                handleCreateNewStatus();
              }}
            >
              <i className="fa-solid fa-circle-plus fs-10 m-0 p-0"></i>
              <span className={"ps-2"}>Add New</span>
            </button>
          </div>
        </div>
      </div>
      {searchable && (
        <div className={"settings-status-list__search"}>
          <Input
            type="text"
            bsSize="sm"
            value={search}
            placeholder="Search…"
            onChange={(e) => setSearch(e.target.value)}
          />
          {reorderable && searchActive && (
            <span
              className={"settings-status-list__search-hint text-muted small"}
            >
              Clear search to reorder
            </span>
          )}
        </div>
      )}
      {(() => {
        const rows = statusList?.map((item, index) => {
          if (!matchesSearch(item)) return null;
          const rowInner = (dragHandle) => (
            <>
              <div className={"p-1 d-flex flex-row align-items-center"}>
                {dragHandle}
                <div>
                  <span className={"d-flex pe-2"} style={{ width: "45px" }}>
                    #{item?.Id}
                  </span>
                </div>

                <div
                  className={"badge d-flex align-items-center"}
                  style={{
                    color: item?.CssColourText ?? "#000000",
                    background: item?.CssColourBackground ?? "#ffffff",
                  }}
                >
                  <div
                    className={
                      "d-flex align-items-center cursor-pointer status-edit-and-name-block"
                    }
                    title={"Click to edit"}
                    onClick={() => {
                      // set as editable true and others false
                      setStatusList((prev) => {
                        const temp = [...prev];
                        temp.map((item) => {
                          item.editable = false;
                          return item;
                        });
                        temp[index].editable = true;
                        return temp;
                      });
                      // setUpdateItemStatus({});
                    }}
                  >
                    {item?.editable ? (
                      <input
                        autoFocus={true}
                        className={"w-100"}
                        type="text"
                        value={item?.Name ?? ""}
                        onChange={(e) => {
                          setStatusList((prev) => {
                            const temp = [...prev];
                            temp[index] = {
                              ...temp[index],
                              Name: e.target.value,
                            };
                            return temp;
                          });
                        }}
                        onBlur={() => {
                          setStatusList((prev) => {
                            const temp = [...prev];
                            temp[index].editable = false;
                            return temp;
                          });
                          setUpdateItemStatus(item);
                        }}
                        // onKeyDown={(e) => {
                        //   if (e.key === "Enter") {
                        //     setStatusList((prev) => {
                        //       const temp = [...prev];
                        //       temp[index].editable = false;
                        //       return temp;
                        //     });
                        //   }
                        //   setUpdateItemStatus(item);
                        // }}
                      />
                    ) : (
                      <span>
                        {item?.Name?.length > 30
                          ? item?.Name?.substring(0, 30) + "..."
                          : item?.Name}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {config &&
                config?.name !== "MasterExternalIdentificationProvider" && (
                  <div className={"d-flex align-items-center"}>
                    <div className={"ms-2 me-2"}>
                      <button
                        className={
                          item?.IntervalValueMathSign === "+"
                            ? "btn btn-sm btn-success me-1"
                            : "btn btn-sm btn-soft-success me-1"
                        }
                        onClick={() => {
                          setStatusList((prev) => {
                            const temp = [...prev];
                            temp[index].IntervalValueMathSign = "+";
                            return temp;
                          });
                        }}
                      >
                        Add
                      </button>{" "}
                      <button
                        className={
                          item?.IntervalValueMathSign === "-"
                            ? "btn btn-sm btn-danger"
                            : "btn btn-sm btn-soft-danger"
                        }
                        onClick={() => {
                          setStatusList((prev) => {
                            const temp = [...prev];
                            temp[index].IntervalValueMathSign = "-";
                            return temp;
                          });
                        }}
                      >
                        Minus
                      </button>
                    </div>
                    <div>
                      <span>Value </span>
                      <input
                        type={"number"}
                        className={"p-2"}
                        style={{ width: "50px" }}
                        value={item?.IntervalValue}
                        onChange={(e) => {
                          setStatusList((prev) => {
                            const temp = [...prev];
                            temp[index].IntervalValue = e.target.value;
                            return temp;
                          });
                        }}
                        onBlur={() => {
                          setUpdateItemStatus(item);
                        }}
                      />
                    </div>
                    <div className={"  ms-2 me-2"} style={{ width: "100px" }}>
                      <Select
                        className={"w-100 p-0 m-0"}
                        onChange={(e) => {
                          setUpdateItemStatus({
                            ...item,
                            MasterPaymentProvider_Id: e.value,
                          });
                        }}
                        value={{
                          value: item?.MasterPaymentProvider_Id,
                          label: config?.data?.find(
                            (dataItem) =>
                              dataItem.Id === item?.MasterPaymentProvider_Id
                          )?.Name,
                          id: item?.MasterPaymentProvider_Id,
                        }}
                        options={config.data?.map((item) => ({
                          value: item.Id,
                          label: item.Name,
                          id: item.Id,
                        }))}
                      ></Select>
                    </div>
                  </div>
                )}
              {config?.name === "MasterExternalIdentificationProvider" && (
                <div
                  className={"d-flex align-items-center ms-2 me-2"}
                  style={{ width: "180px" }}
                >
                  <Select
                    className={"w-100 p-0 m-0"}
                    placeholder={"Map to master…"}
                    onChange={(e) => {
                      setUpdateItemStatus({
                        ...item,
                        MasterExternalIdentificationProvider_Id: e.value,
                      });
                    }}
                    value={
                      item?.MasterExternalIdentificationProvider_Id
                        ? {
                            value:
                              item?.MasterExternalIdentificationProvider_Id,
                            label: config?.data?.find(
                              (d) =>
                                d.Id ===
                                item?.MasterExternalIdentificationProvider_Id
                            )?.Name,
                            id: item?.MasterExternalIdentificationProvider_Id,
                          }
                        : ""
                    }
                    options={config.data?.map((d) => ({
                      value: d.Id,
                      label: d.Name,
                      id: d.Id,
                    }))}
                  ></Select>
                </div>
              )}
              {/*  colorpicker input for text color*/}
              <div className={"ms-2 me-2 d-flex align-items-center"}>
                <span>Text Colour: </span>
                <input
                  // style={{ background: item?.CssColourText }}
                  className={"color-picker-like-input-circle"}
                  value={item?.CssColourText ?? "#000000"}
                  type="color"
                  onChange={(e) => {
                    setStatusList((prev) => {
                      const temp = [...prev];
                      temp[index].CssColourText = e.target.value;
                      return temp;
                    });
                  }}
                  // after color selected set update values
                  onBlur={() => {
                    setUpdateItemStatus(item);
                  }}
                />
              </div>
              <div className={"m-0 ms-2 me-2 d-flex align-items-center"}>
                <span>Background Colour: </span>
                <input
                  // style={{ background: item?.CssColourBackground }}
                  className={"color-picker-like-input-circle"}
                  value={item?.CssColourBackground ?? "#000000"}
                  type="color"
                  onChange={(e) => {
                    setStatusList((prev) => {
                      const temp = [...prev];
                      temp[index].CssColourBackground = e.target.value;
                      return temp;
                    });
                  }}
                  onBlur={() => {
                    setUpdateItemStatus(item);
                  }}
                />
              </div>
              {costOfGoodsSold && (
                <div className={"m-0 ms-2 me-2 d-flex align-items-center"}>
                  {/*  check box*/}

                  <span className={"d-flex align-items-center me-2"}>
                    Cost of goods sold
                  </span>
                  <input
                    type="checkbox"
                    className="form-check-input m-0"
                    checked={item?.FlagCostOfGoodsSold === "1"}
                    onChange={(e) => {
                      setStatusList((prev) => {
                        const temp = [...prev];
                        temp[index].FlagCostOfGoodsSold = e.target.checked
                          ? "1"
                          : "0";
                        return temp;
                      });
                      setUpdateItemStatus(item);
                    }}
                  />
                </div>
              )}
              {(statusFlag || bankAccountStatus) && (
                <div className={"m-0 ms-2 me-2 d-flex align-items-center"}>
                  {/*  check box*/}
                  <span className={"d-flex align-items-center me-2"}>
                    {statusFlag && <span>Mark as Status Complete</span>}
                    {bankAccountStatus && <span>Active</span>}
                  </span>
                  <input
                    type="checkbox"
                    className="form-check-input m-0"
                    checked={item?.FlagActive === "1"}
                    onChange={(e) => {
                      setStatusList((prev) => {
                        const temp = [...prev];
                        temp[index].FlagActive = e.target.checked ? "1" : "0";
                        return temp;
                      });
                      setUpdateItemStatus(item);
                    }}
                  />
                </div>
              )}
              {statusFlag && (
                <div className={"m-0 ms-2 me-2 d-flex align-items-center"}>
                  {/*  check box*/}
                  <span className={"d-flex align-items-center me-2"}>
                    Invoiceable
                  </span>
                  <input
                    type="checkbox"
                    className="form-check-input m-0"
                    checked={item?.FlagInvoiceable === "1"}
                    onChange={(e) => {
                      setStatusList((prev) => {
                        const temp = [...prev];
                        temp[index].FlagInvoiceable = e.target.checked
                          ? "1"
                          : "0";
                        return temp;
                      });
                      setUpdateItemStatus(item);
                    }}
                  />
                </div>
              )}
              {allowUnbilledCloseFlag && (
                <div
                  className={"m-0 ms-2 me-2 d-flex align-items-center"}
                  title={ALLOW_UNBILLED_CLOSE_HINT}
                >
                  <span className={"d-flex align-items-center me-2"}>
                    Allow closing with unbilled items
                  </span>
                  <input
                    type="checkbox"
                    className="form-check-input m-0"
                    checked={item?.FlagAllowUnbilledClose === "1"}
                    onChange={(e) => {
                      setStatusList((prev) => {
                        const temp = [...prev];
                        temp[index].FlagAllowUnbilledClose = e.target.checked
                          ? "1"
                          : "0";
                        return temp;
                      });
                      setUpdateItemStatus(item);
                    }}
                  />
                </div>
              )}
              {mileageBased && (
                <div className={"m-0 ms-2 me-2 d-flex align-items-center"}>
                  {/*  check box*/}
                  <span className={"d-flex align-items-center me-2"}>
                    Mileage Based
                  </span>
                  <input
                    type="checkbox"
                    className="form-check-input m-0"
                    checked={item?.FlagMileageBased === "1"}
                    onChange={(e) => {
                      setStatusList((prev) => {
                        const temp = [...prev];
                        temp[index].FlagMileageBased = e.target.checked
                          ? "1"
                          : "0";
                        return temp;
                      });
                      setUpdateItemStatus(item);
                    }}
                  />
                </div>
              )}
              {renderCheckTypeSchedule(item, (patch) => {
                const updated = { ...item, ...patch };
                setStatusList((prev) => {
                  const temp = [...prev];
                  temp[index] = updated;
                  return temp;
                });
                setUpdateItemStatus(updated);
              })}
              {paymentTerm && (
                <div style={{ width: "350px" }} className={"me-2"}>
                  <div className={"d-flex flex-row align-items-center  "}>
                    <div className={"me-1"} style={{ width: "100px" }}>
                      Logic Type
                    </div>
                    <Select
                      placeholder={"Select Logic Type"}
                      className={"form-control  p-0 m-0"}
                      value={
                        item?.LogicType
                          ? {
                              value: item?.LogicType,
                              label: logicType?.find(
                                (logic) => logic.value === item?.LogicType
                              )?.label,
                            }
                          : []
                      }
                      onChange={(e) => {
                        setStatusList((prev) => {
                          const temp = [...prev];
                          temp[index].LogicType = e.value;
                          return temp;
                        });
                        setUpdateItemStatus(item);
                      }}
                      options={logicType}
                    ></Select>
                  </div>
                </div>
              )}
              {paymentTerm &&
                (item?.LogicType === "1" ||
                  item?.LogicType === "2" ||
                  item?.LogicType === "3") && (
                  <div className={"me-2"}>
                    <div className={"d-flex flex-row align-items-center  "}>
                      <div className={"me-1"}>Duration</div>
                      <input
                        type={"number"}
                        className={"p-2"}
                        style={{ width: "50px" }}
                        value={item?.LogicValue}
                        onChange={(e) => {
                          setStatusList((prev) => {
                            const temp = [...prev];
                            temp[index].LogicValue = e.target.value;
                            return temp;
                          });
                        }}
                        onBlur={() => {
                          setUpdateItemStatus(item);
                        }}
                      />
                    </div>
                  </div>
                )}
              {/*Task Creation*/}
              <div className={"m-0 ms-2 me-2 d-flex align-items-center"}>
                <span className={"d-flex align-items-center me-2"}>
                  <span>Mark Completed </span>
                </span>
                <input
                  type="checkbox"
                  className="form-check-input m-0"
                  checked={item?.FlagComplete === "1"}
                  onChange={(e) => {
                    setStatusList((prev) => {
                      const temp = [...prev];
                      temp[index].FlagComplete = e.target.checked ? "1" : "0";
                      return temp;
                    });
                    setUpdateItemStatus(item);
                  }}
                />
              </div>

              {taskCustomStatus && (
                <div className={"d-flex flex-row ms-3 me-3 align-items-center"}>
                  {item?.TriggerTaskCreationTaskTemplateId && (
                    <div className={"m-0 ms-2 me-2 d-flex align-items-center"}>
                      <span className={"d-flex align-items-center me-2"}>
                        <span>Auto Task Creation</span>
                      </span>
                      <input
                        type="checkbox"
                        className="form-check-input m-0"
                        checked={item?.FlagTriggerTaskCreation === "1"}
                        onChange={(e) => {
                          setStatusList((prev) => {
                            const temp = [...prev];
                            temp[index].FlagTriggerTaskCreation = e.target
                              .checked
                              ? "1"
                              : "0";
                            return temp;
                          });
                          setUpdateItemStatus(item);
                        }}
                      />
                    </div>
                  )}

                  <div>
                    {!isEmpty(taskTemplateList) ? (
                      <div style={{ width: "250px" }} className={"me-2 mt-2"}>
                        <Select
                          // add clear
                          isClearable={true}
                          className={"form-control  p-0 m-0"}
                          value={
                            item?.TriggerTaskCreationTaskTemplateId
                              ? {
                                  value:
                                    item?.TriggerTaskCreationTaskTemplateId,
                                  label: taskTemplateList?.find(
                                    (t) =>
                                      t.Id ===
                                      item?.TriggerTaskCreationTaskTemplateId
                                  )?.Title,
                                }
                              : null
                          }
                          placeholder={"Select Task Template"}
                          onChange={(e) => {
                            setStatusList((prev) => {
                              const temp = [...prev];
                              temp[index].TriggerTaskCreationTaskTemplateId = e
                                ? e.value
                                : null;
                              return temp;
                            });
                            setUpdateItemStatus(item);
                          }}
                          //on clear set value to null
                          onClear={() => {
                            setStatusList((prev) => {
                              const temp = [...prev];
                              temp[index].TriggerTaskCreationTaskTemplateId =
                                null;
                              return temp;
                            });
                            setUpdateItemStatus(item);
                          }}
                          options={taskTemplateList?.map((t) => ({
                            value: t.Id,
                            label: t.Title,
                          }))}
                        ></Select>
                      </div>
                    ) : (
                      ""
                    )}
                  </div>
                </div>
              )}
              <div>
                <button
                  title={"Delete"}
                  className={"btn btn-sm btn-danger status-recycling-bin-icon"}
                  onClick={() => {
                    setDeleteModal(true);
                    setDeleteStatus(item.Id);
                  }}
                >
                  <i className="fa-solid fa-trash-can fs-10 m-0 p-0"></i>
                  <span className={"ps-2"}>Delete</span>
                </button>
              </div>
            </>
          );
          return dndEnabled ? (
            <SortableRow key={item.Id ?? index} id={String(item.Id)}>
              {rowInner}
            </SortableRow>
          ) : (
            <div
              key={item.Id ?? index}
              className={
                "settings-status-list__row d-flex flex-row align-items-center justify-content-start"
              }
            >
              {rowInner(reorderable ? staticGrip : null)}
            </div>
          );
        });
        return dndEnabled ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            modifiers={[restrictToVerticalAxis, restrictToParentElement]}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={(statusList || []).map((s) => String(s.Id))}
              strategy={verticalListSortingStrategy}
            >
              {rows}
            </SortableContext>
          </DndContext>
        ) : (
          rows
        );
      })()}
      {searchActive && visibleCount === 0 && (
        <div className={"text-muted small px-1"}>
          No matches for “{search}”.
        </div>
      )}
    </div>
  );
}
